import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FullHttpParameterCodec } from '../../../shared/http/full-parameter-codec';
import { map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Contact,
  ContactRequest,
  ContactSearchParams,
  PageResponse,
  ContactState,
  ContactCycle,
  ContactOrigin,
  ContactAttributeDefinition,
  ContactAttributeCustomResponse,
  ContactAttributeCustomRequest,
  ContactAssociationsSummary,
  ContactBulkActionRequest,
  ContactBulkActionResponse
} from '../models/contacto.model';
import { ImportMode, ImportResultResponse } from '../../../shared/models/import-export.model';
import { CrmUserResponse } from '../../users/models/user.model';

@Injectable({
  providedIn: 'root'
})
export class ContactoApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // GET /api/contacts - List contacts with filters and pagination
  searchContacts(params: ContactSearchParams): Observable<PageResponse<Contact>> {
    let httpParams = new HttpParams({ encoder: new FullHttpParameterCodec() })
      .set('businessId', params.businessId.toString());

    if (params.phonePrefix) {
      httpParams = httpParams.set('phonePrefix', params.phonePrefix);
    }

    if (params.idContactState !== undefined) {
      httpParams = httpParams.set('idContactState', params.idContactState.toString());
    }
    if (params.idContactCycle !== undefined) {
      httpParams = httpParams.set('idContactCycle', params.idContactCycle.toString());
    }
    if (params.idOrigin !== undefined) {
      httpParams = httpParams.set('idOrigin', params.idOrigin.toString());
    }
    if (params.searchTerm) {
      httpParams = httpParams.set('searchTerm', params.searchTerm);
    }
    if (params.companySearchTerm) {
      httpParams = httpParams.set('companySearchTerm', params.companySearchTerm);
    }
    if (params.attributeDefinitionId !== undefined) {
      httpParams = httpParams.set('attributeDefinitionId', params.attributeDefinitionId.toString());
    }
    if (params.attrStrValue) {
      httpParams = httpParams.set('attrStrValue', params.attrStrValue);
    }
    if (params.attrIntValue !== undefined) {
      httpParams = httpParams.set('attrIntValue', params.attrIntValue.toString());
    }
    if (params.attrBolValue !== undefined) {
      httpParams = httpParams.set('attrBolValue', params.attrBolValue.toString());
    }
    if (params.attrStrListValue) {
      httpParams = httpParams.set('attrStrListValue', params.attrStrListValue);
    }
    if (params.isActive !== undefined) {
      httpParams = httpParams.set('isActive', params.isActive.toString());
    }
    if (params.userPropietyId !== undefined) {
      httpParams = httpParams.set('userPropietyId', params.userPropietyId.toString());
    }
    if (params.withoutOwner !== undefined) {
      httpParams = httpParams.set('withoutOwner', params.withoutOwner.toString());
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<PageResponse<Contact>>(`${this.apiUrl}/contacts`, { params: httpParams });
  }

  // GET /api/contacts/{id} - Get contact by ID
  getContactById(id: number): Observable<Contact> {
    return this.http.get<Contact>(`${this.apiUrl}/contacts/${id}`);
  }

  // POST /api/contacts - Create new contact
  createContact(contact: ContactRequest): Observable<Contact> {
    return this.http.post<Contact>(`${this.apiUrl}/contacts`, contact);
  }

  // PUT /api/contacts/{id} - Update existing contact
  updateContact(id: number, contact: ContactRequest): Observable<Contact> {
    return this.http.put<Contact>(`${this.apiUrl}/contacts/${id}`, contact);
  }

  // DELETE /api/contacts/{id} - Delete contact (409 if hasAssociations)
  deleteContact(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/contacts/${id}`);
  }

  // DELETE /api/contacts/{id}/unlink-and-delete - Auto-desvincula campañas + empresas y elimina.
  // Mantiene el bloqueo (409 CONTACT_HAS_ASSOCIATIONS) por asociaciones fuertes. Requiere contacto inactivo.
  unlinkAndDeleteContact(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/contacts/${id}/unlink-and-delete`);
  }

  // GET /api/contacts/{id}/associations-summary - per-type counts + hasAssociations flag
  getAssociationsSummary(id: number): Observable<ContactAssociationsSummary> {
    return this.http.get<ContactAssociationsSummary>(`${this.apiUrl}/contacts/${id}/associations-summary`);
  }

  // GET /api/contact-states?businessId=<id> - List contact states for a business (paginado)
  getContactStates(businessId: number): Observable<ContactState[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PageResponse<ContactState>>(`${this.apiUrl}/contact-states`, { params })
      .pipe(map(res => res.content));
  }

  // GET /api/contact-cycles?businessId=<id> - List contact cycles for a business (paginado desde v1.16)
  getContactCycles(businessId: number): Observable<ContactCycle[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PageResponse<ContactCycle>>(`${this.apiUrl}/contact-cycles`, { params })
      .pipe(map(res => res.content));
  }

  // GET /api/contact-origins?businessId=&page=&size= - List contact origins (paginado, requiere businessId desde v1.16)
  getContactOrigins(businessId: number): Observable<ContactOrigin[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PageResponse<ContactOrigin>>(`${this.apiUrl}/contact-origins`, { params })
      .pipe(map(res => res.content));
  }

  // POST /api/contacts/{id}/change-cycle - Change contact cycle (v1.16)
  changeContactCycle(contactId: number, cycleId: number): Observable<Contact> {
    return this.http.post<Contact>(`${this.apiUrl}/contacts/${contactId}/change-cycle`, { cycleId });
  }

  // PATCH /api/contacts/{id}/archive - Archive a contact
  archiveContact(id: number): Observable<Contact> {
    return this.http.patch<Contact>(`${this.apiUrl}/contacts/${id}/archive`, null);
  }

  // PATCH /api/contacts/{id}/restore - Restore an archived contact
  restoreContact(id: number): Observable<Contact> {
    return this.http.patch<Contact>(`${this.apiUrl}/contacts/${id}/restore`, null);
  }

  // PATCH /api/contacts/{id}/change-owner - Change contact owner (Manager+)
  changeContactOwner(id: number, userPropietyId: number): Observable<Contact> {
    return this.http.patch<Contact>(`${this.apiUrl}/contacts/${id}/change-owner`, { userPropietyId });
  }

  // GET /api/contact-attribute-definitions?businessId=X&page=0&size=100
  getAttributeDefinitions(businessId: number): Observable<ContactAttributeDefinition[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PageResponse<ContactAttributeDefinition>>(`${this.apiUrl}/contact-attribute-definitions`, { params })
      .pipe(map(res => res.content));
  }

  // GET /api/contact-attributes-custom?contactId=X&businessId=Y&page=0&size=100
  getContactCustomAttributes(contactId: number, businessId: number): Observable<ContactAttributeCustomResponse[]> {
    const params = new HttpParams()
      .set('contactId', contactId.toString())
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PageResponse<ContactAttributeCustomResponse>>(`${this.apiUrl}/contact-attributes-custom`, { params })
      .pipe(map(res => res.content));
  }

  /**
   * Búsqueda filtrada de atributos custom de un contacto.
   * Soporta filtrado server-side por tipo y valor según la API v1.16+:
   *   - strValue     → ILIKE parcial (text)
   *   - intValue     → exacto (number)
   *   - bolValue     → exacto (boolean)
   *   - strListValue → ILIKE parcial (list)
   */
  searchContactCustomAttributes(params: {
    contactId: number;
    businessId: number;
    attributeType?: string;
    strValue?: string;
    intValue?: number | null;
    bolValue?: boolean | null;
    strListValue?: string;
    page?: number;
    size?: number;
  }): Observable<ContactAttributeCustomResponse[]> {
    let httpParams = new HttpParams()
      .set('contactId', params.contactId.toString())
      .set('businessId', params.businessId.toString())
      .set('page', (params.page ?? 0).toString())
      .set('size', (params.size ?? 20).toString());

    if (params.attributeType)              httpParams = httpParams.set('attributeType', params.attributeType);
    if (params.strValue != null && params.strValue !== '')  httpParams = httpParams.set('strValue', params.strValue);
    if (params.intValue != null)           httpParams = httpParams.set('intValue', params.intValue.toString());
    if (params.bolValue != null)           httpParams = httpParams.set('bolValue', params.bolValue.toString());
    if (params.strListValue != null && params.strListValue !== '') httpParams = httpParams.set('strListValue', params.strListValue);

    return this.http.get<PageResponse<ContactAttributeCustomResponse>>(
      `${this.apiUrl}/contact-attributes-custom`, { params: httpParams }
    ).pipe(map(res => res.content));
  }

  // POST /api/contact-attributes-custom
  createCustomAttribute(req: ContactAttributeCustomRequest): Observable<ContactAttributeCustomResponse> {
    return this.http.post<ContactAttributeCustomResponse>(`${this.apiUrl}/contact-attributes-custom`, req);
  }

  // PUT /api/contact-attributes-custom/{id}
  updateCustomAttribute(id: number, req: ContactAttributeCustomRequest): Observable<ContactAttributeCustomResponse> {
    return this.http.put<ContactAttributeCustomResponse>(`${this.apiUrl}/contact-attributes-custom/${id}`, req);
  }

  // DELETE /api/contact-attributes-custom/{id}
  deleteCustomAttribute(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/contact-attributes-custom/${id}`);
  }

  // POST /api/contacts/bulk-action - Acción masiva (CHANGE_OWNER | ARCHIVE | DELETE) sobre contactos.
  // businessId/userId salen del token (no enviar). Los fallos por contacto vienen en 200 dentro de failures[].
  bulkActionContacts(req: ContactBulkActionRequest): Observable<ContactBulkActionResponse> {
    return this.http.post<ContactBulkActionResponse>(`${this.apiUrl}/contacts/bulk-action`, req);
  }

  // GET /api/auth/users/selectable?businessId - Usuarios activos del negocio (para el picker de responsable).
  // Endpoint sin permiso 'users:VIEW' (solo aislamiento por tenant): roles como Ventas pueden poblar
  // el selector. El listado completo /auth/users exige users:VIEW y devuelve 403 a esos roles.
  getUsersByBusiness(businessId: number): Observable<CrmUserResponse[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString());
    return this.http.get<CrmUserResponse[]>(`${this.apiUrl}/auth/users/selectable`, { params })
      .pipe(map(r => r ?? []));
  }

  // GET /api/contacts/export — descarga CSV con todos los contactos del business (Admin+)
  exportContactsCsv(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/contacts/export`, { responseType: 'blob' });
  }

  // GET /api/contacts/import/template — descarga CSV vacío con el header (Admin+)
  downloadContactsImportTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/contacts/import/template`, { responseType: 'blob' });
  }

  // POST /api/contacts/import?mode=... — sube CSV y procesa (Admin+)
  importContactsCsv(file: File, mode: ImportMode = 'SKIP'): Observable<ImportResultResponse> {
    const form = new FormData();
    form.append('file', file);
    const params = new HttpParams().set('mode', mode);
    return this.http.post<ImportResultResponse>(`${this.apiUrl}/contacts/import`, form, { params });
  }
}
